/* eslint-disable @typescript-eslint/no-empty-function */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getLogger } from '../utils/SearchLogger';
import LocalStorageImpl from './LocalStorageImpl';
import NullStorageImpl from './NullStorageImpl';
import SessionStorageImpl from './SessionStorageImpl';
import StorageBase from './StorageBase';

export const WEB_STORAGE_KEYS = {
	FLOODGATE_FEATURES: 'searchFloodgateFeatures',
};

export default class WebStorage {
	static sessionStorage = WebStorage.getStorage('sessionStorage');
	static localStorage = WebStorage.getStorage('localStorage');

	/**
	 * This method checks to see if the supplied type of storage is available for use. Taken from https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
	 * @param {string} type the type of storage to check for.
	 */
	static storageAvailable(type: StorageType): boolean {
		let storage;

		try {
			storage = window[type];
			const x = '__storage_test__';
			storage.setItem(x, x);
			storage.removeItem(x);
			return true;
		} catch (e) {
			const error: any = e;
			if (typeof storage === 'undefined') {
				getLogger().warn('Globals', 'Storage Unavailable', error.message);
			}

			// $FlowFixMe
			return (
				e instanceof DOMException && // everything except Firefox
				(e.code === 22 || // Firefox
					e.code === 1014 || // test name field too, because code might not be present
					// everything except Firefox
					e.name === 'QuotaExceededError' || // Firefox
					e.name === 'NS_ERROR_DOM_QUOTA_REACHED') && // acknowledge QuotaExceededError only if there's something already stored
				storage &&
				storage.length !== 0
			);
		}
	}

	static getStorage(type: StorageType): IStorage {
		// start with an empty storage implementation
		let storageImpl = new NullStorageImpl();

		if (WebStorage.storageAvailable(type)) {
			switch (type) {
				case 'localStorage':
					storageImpl = new LocalStorageImpl();
					break;

				case 'sessionStorage':
					storageImpl = new SessionStorageImpl();
					break;
			}
		}

		return new StorageBase(storageImpl);
	}
}
